import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import { Icon, Text } from '../../../../../components';

// Loading spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Step entrance animation
const stepEntrance = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StepsTrackerContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 16px;
  border-radius: 12px;
  margin-top: 20px;
  border: 1px solid var(--border-muted);
  background: var(--elevation-high);
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  position: relative;
  animation: ${stepEntrance} 0.3s ease-out;
  animation-fill-mode: both;
  cursor: ${(props) => (props.isCanned ? 'not-allowed' : 'pointer')};
  background-color: ${(props) =>
    props.isActiveChildQuestion ? 'var(--primary-default)' : 'transparent'};

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 20px;
    left: 7px;
    width: 2px;
    height: calc(100% - 16px);
    background-color: var(--border-muted);
    animation: ${stepEntrance} 0.3s ease-out 0.1s;
    animation-fill-mode: both;
  }
`;

const StepIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 10px;
  background-color: ${(props) => {
    if (props.completed && !props.canned) return 'var(--primary-default)';
    if (props.canned) return 'var(--critical-default)';
    return 'transparent';
  }};
  color: ${(props) => {
    if (props.completed || props.canned) return 'white';
    if (props.active) return 'var(--border-muted)';
    return 'var(--border-muted)';
  }};
  border: none;
  transition: all 0.3s ease;
  flex-shrink: 0;
`;

const SpinningIcon = styled(Icon)`
  animation: ${spin} 1.5s linear infinite;
`;

const StepContent = styled.div`
  flex: 1;
  padding-top: 0;
`;

const StyledText = styled(Text)`
  text-decoration: ${(props) => (props.isActiveChildQuestion ? 'underline' : 'none')};
`;

const StepsTracker = ({ steps, currentStep, setActiveChildQuestion, activeChildQuestion }) => {
  const getStepIndicatorContent = (index, isCompleted, isActive, isCanned) => {
    if (isActive) {
      return <SpinningIcon name='steps-loader' size={16} />;
    }
    if (isCanned) {
      return <Icon name='failed-step' size={16} />;
    }
    if (isCompleted) {
      return <Icon name='step-checkbox' size={16} />;
    }
    return index + 1;
  };

  return (
    <StepsTrackerContainer>
      <StepsContainer>
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;

          return (
            <Step
              key={step.id}
              completed={isCompleted}
              isCanned={step.isCanned}
              onClick={() => !step.isCanned && setActiveChildQuestion(index)}
            >
              <StepIndicator active={isActive} completed={isCompleted} canned={step.isCanned}>
                {getStepIndicatorContent(index, isCompleted, isActive, step.isCanned)}
              </StepIndicator>
              <StepContent>
                <StyledText isActiveChildQuestion={activeChildQuestion === index}>
                  {step.body}
                </StyledText>
              </StepContent>
            </Step>
          );
        })}
      </StepsContainer>
    </StepsTrackerContainer>
  );
};

StepsTracker.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      body: PropTypes.string,
      isCanned: PropTypes.bool
    })
  ).isRequired,
  currentStep: PropTypes.number,
  setActiveChildQuestion: PropTypes.func.isRequired,
  activeChildQuestion: PropTypes.number.isRequired
};

StepsTracker.defaultProps = {
  currentStep: 0
};

export { StepsTracker };
